import React from 'react';
import { Icon } from '@iconify/react';
import { useSubscriptionRenewal } from '../../context/SubscriptionRenewalContext.jsx';
import '../../styles/Auth/profileDropdown.css';

/**
 * Format base validity, extra validity, and display title for package cards & summary
 */
const formatValidityDetails = (pkg) => {
    if (!pkg) return { baseText: '', extraText: '', totalText: '', titleWithValidity: '' };

    const baseVal = pkg.baseValidity || 0;
    const baseUnit = pkg.validityUnit || 'Months';
    const baseText = `${baseVal} ${baseVal === 1 ? baseUnit.replace(/s$/i, '') : baseUnit}`;

    let extraText = '';
    if (pkg.extraValidity && pkg.extraValidity > 0) {
        const extraVal = pkg.extraValidity;
        const extraUnit = pkg.extraValidityUnit || 'Days';
        extraText = `${extraVal} ${extraVal === 1 ? extraUnit.replace(/s$/i, '') : extraUnit} Extra`;
    }

    let titleWithValidity = pkg.packageName || '';
    if (extraText) {
        titleWithValidity = `${pkg.packageName} – ${baseText} + ${extraText}`;
    } else if (baseText) {
        titleWithValidity = `${pkg.packageName} – ${baseText}`;
    }

    let totalText = pkg.totalValidity;
    if (!totalText) {
        if (extraText) {
            totalText = `${baseText} + ${extraText}`;
        } else {
            totalText = baseText;
        }
    }

    return {
        baseText,
        extraText,
        totalText,
        titleWithValidity
    };
};

const RenewSubscriptionModal = () => {
    const {
        isModalOpen,
        isLoading,
        subscriptionData,
        activePackages,
        selectedRenewPackageId,
        renewing,
        setSelectedRenewPackageId,
        closeRenewPackageModal,
        handleRenewSubscription
    } = useSubscriptionRenewal();

    if (!isModalOpen) return null;

    return (
        <div className="renewal-modal-overlay" onClick={closeRenewPackageModal}>
            <div className="renewal-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="renewal-modal-header">
                    <div className="modal-title-with-icon">
                        <Icon icon="solar:restart-bold-duotone" className="modal-header-icon" />
                        <h3>Renew Store Subscription</h3>
                    </div>
                    <button className="close-modal-btn" onClick={closeRenewPackageModal} aria-label="Close modal">
                        <Icon icon="solar:close-circle-bold-duotone" />
                    </button>
                </div>

                <div className="renewal-modal-body">
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Icon icon="line-md:loading-twotone-loop" style={{ fontSize: '38px', color: '#3C0008' }} />
                            <p style={{ marginTop: '12px', color: '#475569', fontWeight: '600', fontSize: '15px' }}>
                                Loading subscription packages...
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Current Store & Active Subscription Info */}
                            <div className="renewal-info-box">
                                <div className="renewal-info-row">
                                    <div className="info-item">
                                        <span className="info-label">Store</span>
                                        <span className="info-value">{subscriptionData?.storeName || 'Current Store'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Current Active Package</span>
                                        <span className="info-value highlight">{subscriptionData?.packageName || 'None'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Current Expiry Date</span>
                                        <span className="info-value expiry">
                                            {subscriptionData?.finalExpiryDate 
                                                ? new Date(subscriptionData.finalExpiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) 
                                                : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Package Selection Area */}
                            <div className="form-group-renew">
                                <label className="form-label-renew">Select Subscription Package for Renewal *</label>
                                <div className="renew-package-cards-container">
                                    {activePackages.length > 0 ? activePackages.map(pkg => {
                                        let total = pkg.totalAmount;
                                        if (total === undefined) {
                                            if (pkg.isGstApplicable && pkg.gstType === 'Exclusive') {
                                                total = (pkg.packagePrice || 0) + ((pkg.packagePrice || 0) * (pkg.gstPercentage || 0) / 100);
                                            } else {
                                                total = pkg.packagePrice || 0;
                                            }
                                        }
                                        total = Math.round(total || 0);

                                        const { baseText, extraText, titleWithValidity } = formatValidityDetails(pkg);
                                        const isSelected = selectedRenewPackageId === pkg._id;

                                        return (
                                            <div 
                                                key={pkg._id} 
                                                className={`renew-package-card ${isSelected ? 'selected' : ''}`}
                                                onClick={() => setSelectedRenewPackageId(pkg._id)}
                                            >
                                                <div className="renew-card-left">
                                                    <div className="renew-pkg-header">
                                                        <Icon 
                                                            icon={isSelected ? "solar:check-circle-bold-duotone" : "solar:circle-linear"} 
                                                            className="renew-pkg-icon" 
                                                        />
                                                        <span className="renew-pkg-title">{titleWithValidity}</span>
                                                    </div>

                                                    <div className="renew-pkg-validity-breakdown">
                                                        <span className="validity-tag base-tag">
                                                            <Icon icon="solar:calendar-bold-duotone" /> Base: {baseText}
                                                        </span>
                                                        {extraText && (
                                                            <span className="validity-tag extra-tag">
                                                                <Icon icon="solar:gift-bold-duotone" /> Offer: +{extraText}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="renew-pkg-right">
                                                    <div className="renew-pkg-price">₹{total.toLocaleString('en-IN')}</div>
                                                    <span className="gst-note">
                                                        {pkg.isGstApplicable ? `(Incl. GST)` : `(No GST)`}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    }) : (
                                        <div className="no-packages-msg">No active packages available for renewal.</div>
                                    )}
                                </div>
                            </div>

                            {/* Selected Package Details Summary */}
                            {selectedRenewPackageId && (() => {
                                const selectedPkg = activePackages.find(p => p._id === selectedRenewPackageId);
                                if (!selectedPkg) return null;

                                let base = selectedPkg.packagePrice || 0;
                                let gst = selectedPkg.gstAmount;
                                let total = selectedPkg.totalAmount;

                                if (total === undefined) {
                                    if (selectedPkg.isGstApplicable && selectedPkg.gstType === 'Exclusive') {
                                        gst = (base * (selectedPkg.gstPercentage || 0)) / 100;
                                        total = base + gst;
                                    } else if (selectedPkg.isGstApplicable && selectedPkg.gstType === 'Inclusive') {
                                        total = base;
                                        gst = base - (base / (1 + (selectedPkg.gstPercentage || 0) / 100));
                                        base = total - gst;
                                    } else {
                                        gst = 0;
                                        total = base;
                                    }
                                }
                                total = Math.round(total || 0);

                                const { baseText, extraText, totalText, titleWithValidity } = formatValidityDetails(selectedPkg);

                                return (
                                    <div className="pkg-preview-card">
                                        <div className="preview-header">
                                            <Icon icon="solar:bill-list-bold-duotone" className="preview-icon" />
                                            <h4>Selected Package Summary & Breakdown</h4>
                                        </div>

                                        <div className="pkg-preview-content">
                                            <div className="preview-pkg-name-banner">
                                                <span className="banner-title">{titleWithValidity}</span>
                                            </div>

                                            <div className="validity-summary-grid">
                                                <div className="val-item">
                                                    <span className="val-label">Base Duration:</span>
                                                    <span className="val-value">{baseText}</span>
                                                </div>
                                                <div className="val-item">
                                                    <span className="val-label">Extra Validity Offer:</span>
                                                    <span className={`val-value ${extraText ? 'extra-highlight' : ''}`}>
                                                        {extraText ? `+ ${extraText}` : 'None'}
                                                    </span>
                                                </div>
                                                <div className="val-item full-width">
                                                    <span className="val-label">Total Package Duration:</span>
                                                    <span className="val-value total-val">{totalText}</span>
                                                </div>
                                            </div>

                                            <div className="pricing-summary-table">
                                                <div className="price-row">
                                                    <span>Package Base Price:</span>
                                                    <strong>₹{base.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                                </div>
                                                <div className="price-row">
                                                    <span>
                                                        GST ({selectedPkg.gstPercentage || 0}% {selectedPkg.gstType || 'None'}):
                                                    </span>
                                                    <strong>₹{(gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                                </div>
                                                <div className="price-row total-payable">
                                                    <span>Total Amount Payable:</span>
                                                    <span className="total-amount">₹{total.toLocaleString('en-IN')}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="renewal-queue-note">
                                            <Icon icon="solar:info-circle-bold-duotone" className="note-icon" />
                                            <div>
                                                <strong>Queued Renewal Notice:</strong> If you currently have an active subscription, this new package will be queued and will automatically activate only after your current package expires.
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>

                <div className="renewal-modal-footer">
                    <button 
                        className="btn-cancel-renew" 
                        onClick={closeRenewPackageModal}
                        disabled={renewing || isLoading}
                    >
                        Cancel
                    </button>
                    <button 
                        className="btn-confirm-renew" 
                        onClick={() => handleRenewSubscription()}
                        disabled={renewing || isLoading || !selectedRenewPackageId}
                    >
                        {renewing ? (
                            <>
                                <Icon icon="line-md:loading-twotone-loop" style={{ marginRight: '8px' }} />
                                Processing Renewal...
                            </>
                        ) : (
                            "Confirm Subscription Renewal"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RenewSubscriptionModal;

